import React from 'react';
import Lightbox from 'react-awesome-lightbox';
import 'react-awesome-lightbox/build/style.css';
import { Link } from 'react-router-dom';
import {
    Search, ChevronDown, ChevronUp, X, SlidersHorizontal,
    Grid3X3, List, Map,
} from 'lucide-react';
import i18n from '../../../../i18n';
import { getCategories } from '../../../../models/categories';
import { getOrganizations } from '../../../../models/organization';
import AuctionCardNew from '../../components/Auction/AuctionCard';
import AuctionGroupCardNew from '../../components/Auction/AuctionGroupCard';
import Pagination from '../../../helpers/pagination';
import '../../../../styles/components/auctions-page.scss';
import { SkeletonHomeCard } from '../../../helpers/skeletons/skeletonCards';
import AuctionListCard from './components/AuctionListCard';
import '../../../../styles/components/auction-list-card.scss';
import { SkeletonAuctionCard } from '../../../helpers/skeletons/SkeletonAuctionCard';
import { getAllAuctions } from '../../../../models/auctions';

const current_lng = localStorage.getItem('I18N_LANGUAGE');

export class Auctions extends React.Component {
    constructor() {
        super();
        this.state = {
            total: 0,
            pagination: { limit: 30, page: 1 },
            catArrs: [],
            categories: [],
            organizations: [],
            auctions: [],
            is_loading: false,
            is_cat_loading: false,
            lightboxImages: [],
            is_active_light_box: false,
            grid: true,
            section: '',
            type: '',
            category: '',
            organization: '',
            input: { fdate: '', tdate: '' },
            statusFilter: 'all',
            sortBy: 'newest',
            showSortDropdown: false,
            mobileFiltersOpen: false,
            openSections: { status: true, category: true, seller: false, date: false },
        };
        this.getAllAuctionsList = this.getAllAuctionsList.bind(this);
        this.getImagePopup = this.getImagePopup.bind(this);
        this.applyFilters = this.applyFilters.bind(this);
        this.handleChange = this.handleChange.bind(this);
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        const urlParams = new URLSearchParams(window.location.search);
        const params = Object.fromEntries(urlParams.entries());

        const pagination = {
            limit: params.limit ? parseInt(params.limit) : 30,
            page: params.page ? parseInt(params.page) : 1,
        };
        const sortBy = params.sort || 'newest';
        const statusFilter = params.type || 'all';

        this.setState({ pagination, sortBy, statusFilter }, () => {
            this.loadAllCategories(params);
            this.loadAllOrganizations(params);
            this.getAllAuctionsList(params);
        });
    }

    handleChange(event) {
        const input = { ...this.state.input, [event.target.name]: event.target.value };
        this.setState({ input });
    }

    async loadAllCategories(params) {
        this.setState({ is_cat_loading: true });
        if (params.category) this.setState({ catArrs: [parseInt(params.category)] });
        const response = await getCategories('active=1');
        if (response) this.setState({ categories: response.data, is_cat_loading: false });
    }

    async loadAllOrganizations(params) {
        const q = params.type === 'active' ? 'is_active=1' : '';
        const response = await getOrganizations(q);
        if (response) this.setState({ organizations: response.data });
    }

    async getAllAuctionsList(params) {
        this.setState({ is_loading: true });
        const { pagination, sortBy, grid } = this.state;
        let q = `limit=${pagination.limit}&page=${pagination.page}`;
        if (params.sec) q += `&section=${params.sec}`;
        if (params.type) q += `&type=${params.type}`;
        if (params.category) q += `&category=${params.category}`;
        if (params.organization) q += `&organization=${params.organization}`;
        if (params.search_text) q += `&text=${params.search_text}`;
        if (params.fdate) q += `&fdate=${params.fdate}`;
        if (params.tdate) q += `&tdate=${params.tdate}`;
        q += `&lang=${current_lng}`;
        if (sortBy) q += `&sort=${sortBy}`;
        if (grid) q += `&grouped=1`;

        this.setState({
            section: params.sec,
            type: params.type,
            category: params.category,
            organization: params.organization,
        });

        const response = await getAllAuctions(q);
        if (response) {
            this.setState({ auctions: response.data, total: response.meta.total, is_loading: false });
        }
        this.updateURL(params); // ← add this line
    }
    updateURL(params) {
        const url = new URLSearchParams();
        if (params.type) url.set('type', params.type);
        if (params.sec) url.set('sec', params.sec);
        if (params.category) url.set('category', params.category);
        if (params.organization) url.set('organization', params.organization);
        if (params.search_text) url.set('search_text', params.search_text);
        if (params.fdate) url.set('fdate', params.fdate);
        if (params.tdate) url.set('tdate', params.tdate);
        if (this.state.pagination.page > 1) url.set('page', this.state.pagination.page);
        if (this.state.pagination.limit !== 30) url.set('limit', this.state.pagination.limit);
        if (this.state.sortBy !== 'newest') url.set('sort', this.state.sortBy);

        const qs = url.toString();
        window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);
    }
    toggleCategory = (id) => {
        if (!id) { this.setState({ catArrs: [] }, this.applyFilters); return; }
        let catArrs = [...this.state.catArrs];
        catArrs = catArrs.includes(id) ? catArrs.filter((c) => c !== id) : [...catArrs, id];
        this.setState({ catArrs }, this.applyFilters);
    };

    setStatusFilter = (status) => {
        const map = { all: undefined, active: 'active', upcoming: 'upcoming', previous: 'previous' };
        this.setState({ statusFilter: status, type: map[status] }, this.applyFilters);
    };

    setSortBy = (sortBy) => {
        this.setState({ sortBy, showSortDropdown: false }, this.applyFilters);
    };

    resetFilters = () => {
        this.setState(
            { catArrs: [], statusFilter: 'all', type: '', input: {}, sortBy: 'newest', pagination: { limit: 30, page: 1 } },
            () => this.getAllAuctionsList({})
        );
    };

    applyFilters() {
        const { type, section, catArrs, input, pagination } = this.state;
        pagination.page = 1;
        this.setState({ pagination });
        this.getAllAuctionsList({
            sec: section, type,
            category: catArrs.length > 0 ? catArrs.join(',') : undefined,
            organization: input.organization,
            search_text: input.search_text,
            fdate: input.fdate || undefined,
            tdate: input.tdate || undefined,
        });
    }

    getImagePopup(active, images) {
        const lightboxImages = (images || []).map((img) => ({ url: img.image }));
        this.setState({ is_active_light_box: active, lightboxImages });
    }

    handlePageChange = (pageNumber) => {
        const { type, section, catArrs, input } = this.state;
        const pagination = { ...this.state.pagination, page: pageNumber };
        this.setState({ pagination }, () => {
            this.getAllAuctionsList({
                sec: section, type,
                category: catArrs.length > 0 ? catArrs.join(',') : undefined,
                organization: input.organization,
                search_text: input.search_text,
                fdate: input.fdate || undefined,
                tdate: input.tdate || undefined,
            });
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    };

    toggleSection = (key) => {
        this.setState((prev) => ({
            openSections: { ...prev.openSections, [key]: !prev.openSections[key] },
        }));
    };

    render() {
        const {
            categories, organizations, auctions, catArrs, total, is_loading,
            grid, statusFilter, sortBy, showSortDropdown, openSections, mobileFiltersOpen,
        } = this.state;

        const SORT_OPTIONS = [
            { value: 'newest', label: i18n.t('newest') || 'Newest First' },
            { value: 'oldest', label: i18n.t('oldest') || 'Oldest First' },
        ];

        const STATUS_OPTIONS = [
            { value: 'all', label: i18n.t('all_auctions') },
            { value: 'active', label: i18n.t('active_auctions') },
            { value: 'upcoming', label: i18n.t('upcoming_auctions') },
            { value: 'previous', label: i18n.t('previous_auctions') },
        ];

        return (
            <React.Fragment>
                <div className="mza-page">
                    <div className="mza-results-header">

                        <div className="mza-results-header__inner">
                            <div className="mza-search-bar">
                                <Search size={18} className="mza-search-bar__icon" />
                                <input
                                    type="text"
                                    name="search_text"
                                    className="mza-search-bar__input"
                                    placeholder={i18n.t('search_by_name_loc_seller') || 'Search for an auction or product'}
                                    value={this.state.input.search_text || ''}
                                    onChange={this.handleChange}
                                    onKeyDown={(e) => e.key === 'Enter' && this.applyFilters()}
                                />

                            </div>
                            <button className="mza-search-bar__btn" onClick={() => this.applyFilters()}>
                                {i18n.t('search') || 'Search'}
                            </button>

                            <span className="mza-results-header__count">
                                {is_loading ? '...' : total} {i18n.t('results') || 'results'}
                            </span>
                            <div className="mza-results-header__actions">
                                {(catArrs.length > 0 || statusFilter !== 'all' || this.state.input.search_text) && (
                                    <button className="mza-results-header__reset" onClick={this.resetFilters}>
                                        <X size={14} />
                                        {i18n.t('reset_filters') || 'Reset filters'}
                                    </button>
                                )}
                                <div className="mza-sort">
                                    <button className="mza-sort__trigger" onClick={() => this.setState({ showSortDropdown: !showSortDropdown })}>
                                        <SlidersHorizontal size={14} />
                                        {i18n.t('sort_by') || 'Sort By'}
                                    </button>
                                    {showSortDropdown && (
                                        <div className="mza-sort__dropdown">
                                            {SORT_OPTIONS.map((opt) => (
                                                <button key={opt.value} className={`mza-sort__option ${sortBy === opt.value ? 'mza-sort__option--active' : ''}`} onClick={() => this.setSortBy(opt.value)}>
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="mza-view-toggle">
                                    <button
                                        className={`mza-view-toggle__btn ${grid ? 'mza-view-toggle__btn--active' : ''}`}
                                        onClick={() => this.setState({ grid: true }, this.applyFilters)}
                                    >
                                        <Grid3X3 size={16} />
                                    </button>
                                    <button
                                        className={`mza-view-toggle__btn ${!grid ? 'mza-view-toggle__btn--active' : ''}`}
                                        onClick={() => this.setState({ grid: false }, this.applyFilters)}
                                    >
                                        <List size={16} />
                                    </button>
                                </div>
                                <button className="mza-mobile-filter-btn" onClick={() => this.setState({ mobileFiltersOpen: !mobileFiltersOpen })}>
                                    <SlidersHorizontal size={16} />
                                    {i18n.t('filters') || 'Filters'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ══════════════ MAIN: Sidebar + Grid ══════════════ */}
                    <div className="mza-main">
                        <div className="mza-main__inner">

                            {/* ── Sidebar ── */}
                            <aside className={`mza-sidebar ${mobileFiltersOpen ? 'mza-sidebar--open' : ''}`}>
                                <div className="mza-sidebar__header">
                                    <h3 className="mza-sidebar__title">{i18n.t('filter_results') || 'Filter Results'}</h3>
                                    <button className="mza-sidebar__close" onClick={() => this.setState({ mobileFiltersOpen: false })}>
                                        <X size={20} />
                                    </button>
                                </div>

                                {/* <Link to="/map" className="mza-sidebar__map-btn">
                                    <Map size={16} />
                                    {i18n.t('show_on_map') || 'Show on map'}
                                </Link> */}

                                {/* Status */}
                                <div className="mza-filter-group">
                                    <button className="mza-filter-group__header" onClick={() => this.toggleSection('status')}>
                                        <span>{i18n.t('auction_status') || 'Auction Status'}</span>
                                        {openSections.status ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    </button>
                                    {openSections.status && (
                                        <div className="mza-filter-group__body">
                                            {STATUS_OPTIONS.map((opt) => (
                                                <label key={opt.value} className="mza-radio">
                                                    <input type="radio" name="status" checked={statusFilter === opt.value} onChange={() => this.setStatusFilter(opt.value)} />
                                                    <span className="mza-radio__mark" />
                                                    <span className="mza-radio__label">{opt.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Date Range */}
                                <div className="mza-filter-group">
                                    <button className="mza-filter-group__header" onClick={() => this.toggleSection('date')}>
                                        <span>{i18n.t('date') || 'Date Range'}</span>
                                        {openSections.date ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    </button>
                                    {openSections.date && (
                                        <div className="mza-filter-group__body">
                                            <div className="mza-date-filter">
                                                <label className="mza-date-filter__label">{i18n.t('from_date') || 'From'}</label>
                                                <input
                                                    type="date"
                                                    name="fdate"
                                                    className="mza-date-filter__input"
                                                    value={this.state.input.fdate || ''}
                                                    onChange={(e) => this.handleChange(e)}
                                                />
                                            </div>
                                            <div className="mza-date-filter">
                                                <label className="mza-date-filter__label">{i18n.t('to_date') || 'To'}</label>
                                                <input
                                                    type="date"
                                                    name="tdate"
                                                    className="mza-date-filter__input"
                                                    value={this.state.input.tdate || ''}
                                                    onChange={(e) => this.handleChange(e)}
                                                />
                                            </div>
                                            <button
                                                className="mza-date-filter__apply"
                                                onClick={() => this.applyFilters()}
                                                disabled={!this.state.input.fdate || !this.state.input.tdate}
                                            >
                                                {i18n.t('apply') || 'Apply'}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Seller */}
                                <div className="mza-filter-group">
                                    <button className="mza-filter-group__header" onClick={() => this.toggleSection('seller')}>
                                        <span>{i18n.t('seller') || 'Seller'}</span>
                                        {openSections.seller ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    </button>
                                    {openSections.seller && (
                                        <div className="mza-filter-group__body">
                                            <select className="mza-sidebar__select" name="organization" value={this.state.input.organization || ''} onChange={(e) => { this.handleChange(e); setTimeout(() => this.applyFilters(), 100); }}>
                                                <option value="">{i18n.t('sellers')}</option>
                                                {organizations.map((org) => (
                                                    <option key={org.id} value={org.id}>
                                                        {current_lng === 'ar' ? org.organization_name_ar : org.organization_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>

                                {/* Category */}
                                <div className="mza-filter-group">
                                    <button className="mza-filter-group__header" onClick={() => this.toggleSection('category')}>
                                        <span>{i18n.t('category') || 'Category'}</span>
                                        {openSections.category ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    </button>
                                    {openSections.category && (
                                        <div className="mza-filter-group__body">
                                            {categories.map((cat) => (
                                                <label key={cat.id} className="mza-checkbox">
                                                    <input type="checkbox" checked={catArrs.includes(cat.id)} onChange={() => this.toggleCategory(cat.id)} />
                                                    <span className="mza-checkbox__mark" />
                                                    <span className="mza-checkbox__label">
                                                        {current_lng === 'ar' ? cat.category_name_ar : cat.category_name} ({cat?.total_auctions})
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>


                            </aside>

                            {mobileFiltersOpen && <div className="mza-sidebar-overlay" onClick={() => this.setState({ mobileFiltersOpen: false })} />}

                            {/* ── Auction Grid ── */}
                            <div className="mza-grid-area">
                                {is_loading && (
                                    <SkeletonAuctionCard />
                                )}

                                {!is_loading && auctions.length === 0 && (
                                    <div className="mza-empty">
                                        <Search size={48} />
                                        <p>{i18n.t('no_auctions_found') || 'No auctions found'}</p>
                                    </div>
                                )}

                                {/* Grid view — uses AuctionCardNew */}
                                {!is_loading && grid && auctions.length > 0 && (
                                    <div className="mza-card-grid">
                                        {auctions.map((auction, i) =>
                                            !auction.is_a_group ? (
                                                <div key={auction.id || i} className="mza-card-grid__item">
                                                    <AuctionCardNew
                                                        auction={auction}
                                                        auctionId={auction.id}
                                                    />
                                                </div>
                                            ) : (
                                                <div key={auction.id || i} className="mza-card-grid__item">
                                                    <AuctionGroupCardNew
                                                        group={auction}
                                                        total_items={auctions.length}
                                                        can_bid={false}
                                                        type={this.state.type}
                                                    />
                                                </div>
                                            )
                                        )}
                                    </div>
                                )}

                                {/* List view — horizontal layout */}
                                {!is_loading && !grid && auctions.length > 0 && (
                                    <div className="mza-list-view">
                                        {auctions.map((auction, i) => (
                                            <AuctionListCard key={auction.id || i} auction={auction} />
                                        ))}
                                    </div>
                                )}

                                {/* ── Pagination ── */}
                                {!is_loading && total > this.state.pagination.limit && (
                                    <div className="mza-pagination-wrap">
                                        <span className="mza-pagination-wrap__info">
                                            {i18n.t('page') || 'Page'} {this.state.pagination.page} {i18n.t('of') || 'of'} {Math.ceil(total / this.state.pagination.limit)}
                                        </span>
                                        <Pagination
                                            currentPage={this.state.pagination.page}
                                            totalPages={Math.ceil(total / this.state.pagination.limit)}
                                            onPageChange={this.handlePageChange}
                                        />
                                    </div>
                                )}


                            </div>
                        </div>
                    </div>
                </div>

                {this.state.is_active_light_box && (
                    <Lightbox
                        images={this.state.lightboxImages}
                        onClose={() => this.setState({ is_active_light_box: false })}
                    />
                )}
            </React.Fragment>
        );
    }
}